import React, { createContext, useContext, useState, ReactNode } from 'react';
import ConfirmModal from '../sites/components/common/ConfirmAction';

// Define the context type
interface ConfirmContextType {
  requestConfirmation: (message: string, title?: string) => Promise<boolean>;
}

// Create the context
const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

// Custom hook to use the confirmation context in child components
export const useConfirm = (): ConfirmContextType => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

// Confirmation provider component to wrap the app
const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [confirmResolve, setConfirmResolve] = useState<((value: boolean) => void) | null>(null); // Store resolve function

  // Function to show the confirmation modal and return a Promise
  const requestConfirmation = (message: string, title: string = 'Confirmation') => {
    setModalMessage(message);
    setModalTitle(title); 
    setShowModal(true);

    // Return a promise and set the resolve function
    return new Promise<boolean>((resolve) => {
      setConfirmResolve(() => resolve);  // Set resolve function correctly
    });
  };

  // Handle confirm action
  const handleConfirm = () => {
    setShowModal(false);
    if (confirmResolve) {
      confirmResolve(true); // Resolve with true on confirm
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    setShowModal(false);
    if (confirmResolve) {
      confirmResolve(false); // Resolve with false on cancel
    }
  };

  return (
    <ConfirmContext.Provider value={{ requestConfirmation }}>
      {children}
      {/* Modal will always be rendered but only shown when needed */}
      <ConfirmModal
        show={showModal}
        title={modalTitle} // Pass title to the modal
        message={modalMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
